import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Machine } from '@couch/shared';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { MACHINES_KEY } from '../machines/MachineActions';
import { RECIPES_KEY } from '../RecipesPage';
import { InputImagesPanel, type InputChoices } from './InputImagesPanel';
import { RecipeActions } from './RecipeActions';
import { RunsPanel } from './RunsPanel';
import { BatchPanel } from './BatchPanel';
import { SharePanel } from './SharePanel';
import { WorkflowSummary } from './WorkflowSummary';

function DeleteRecipe({ id, title }: { id: string; title: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const remove = useMutation({
    mutationFn: () => api.deleteRecipe(id),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: RECIPES_KEY }); await navigate('/recipes'); },
  });
  return (
    <>
      <button type="button" className="button-quiet" disabled={remove.isPending} onClick={() => {
        if (window.confirm(`Delete “${title}” with all its versions?`)) remove.mutate();
      }}>Delete</button>
      {remove.isError && <p role="alert" className="form-error">{errorMessage(remove.error)}</p>}
    </>
  );
}

/**
 * A workflow is not bound to a machine (doc 24 B1): preselect the one it last ran on when that is online,
 * else the first online machine — never a machine that is not in the list (removed or replaced).
 */
export function defaultMachineId(list: readonly Machine[], lastRanOn: string | null): string {
  const last = list.find((m) => m.id === lastRanOn);
  if (last?.online) return last.id;
  return list.find((m) => m.online)?.id ?? last?.id ?? list[0]?.id ?? '';
}

export function RecipePage() {
  const { id = '' } = useParams();
  // Only the user's choices are state; the effective values are derived (CLAUDE.md: never seed state from loaded data).
  const [versionOverride, setVersionOverride] = useState(0);
  const [machineOverride, setMachineOverride] = useState('');
  const [inputs, setInputs] = useState<InputChoices>({});
  const detail = useQuery({ queryKey: ['recipe', id, versionOverride], queryFn: () => api.getRecipe(id, versionOverride || undefined) });
  const machines = useQuery({ queryKey: MACHINES_KEY, queryFn: api.listMachines, refetchInterval: 10_000 });
  if (detail.isPending) return <p role="status">Loading…</p>;
  if (detail.isError) return <p role="alert" className="form-error">{errorMessage(detail.error)} <Link to="/workflows">Back to Workflows</Link></p>;
  const { recipe, workflow, versions } = detail.data;
  const list = machines.data?.machines ?? [];
  const machineId = machineOverride || defaultMachineId(list, recipe.machineId);
  const machine = list.find((m) => m.id === machineId);
  return (
    <section aria-labelledby="recipe-title">
      <p><Link to="/workflows">← Workflows</Link></p>
      <div className="page-head recipe-head">
        <h1 id="recipe-title">{recipe.title}</h1>
        <span className="badge">{recipe.author === 'ai' ? 'Made by AI' : 'Made by you'}</span>
        <DeleteRecipe id={recipe.id} title={recipe.title} />
      </div>
      {recipe.intent && <p>{recipe.intent}</p>}
      {recipe.notes && <p className="muted">{recipe.notes}</p>}
      <div className="recipe-pickers">
        <label>Version{' '}
          <select value={versionOverride || recipe.version} onChange={(e) => { setVersionOverride(Number(e.target.value)); }}>
            {versions.map((v) => <option key={v.version} value={v.version}>v{v.version}{v.note ? ` — ${v.note}` : ''}</option>)}
          </select>
        </label>
        <label>Machine{' '}
          <select value={machineId} onChange={(e) => { setMachineOverride(e.target.value); }}>
            {machineId === '' && <option value="">Choose…</option>}
            {list.map((m) => <option key={m.id} value={m.id}>{m.name}{m.online ? '' : ' (offline)'}</option>)}
          </select>
        </label>
      </div>
      <InputImagesPanel machineId={machineId} workflow={workflow} choices={inputs} onChange={setInputs} />
      <RecipeActions recipeId={recipe.id} version={versionOverride || undefined} machine={machine} inputs={inputs} />
      <RunsPanel recipeId={recipe.id} />
      <BatchPanel recipeId={recipe.id} machineId={machineId} workflow={workflow} version={versionOverride || undefined} />
      <SharePanel recipeId={recipe.id} />
      <section className="panel" aria-labelledby="workflow-title">
        <h2 id="workflow-title">Workflow (v{versionOverride || recipe.version})</h2>
        <WorkflowSummary workflow={workflow} />
      </section>
    </section>
  );
}
